import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SoftwareApplicationSchema, BreadcrumbSchema, HowToSchema, OrganizationSchema } from '@/components/SEOSchema';
import { useCalEmbed, openCalModal } from '@/hooks/useCalEmbed';
import { ChevronDown, Video, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import MigrationSwitchSection from '@/components/marketing/MigrationSwitchSection';
import { useTracking, useScrollTracking, useSectionTracking } from '@/hooks/useTracking';
import { useSEO } from '@/hooks/useSEO';
import { SeoHero } from '../components/SeoHero';
import { SeoSection } from '../components/SeoSection';
import { ComparisonTable } from '../components/ComparisonTable';
import { TableOfContents } from '../components/TableOfContents';
import { StickyMobileCTA } from '../components/StickyMobileCTA';
import { TrustStrip } from '../components/TrustStrip';
import { Callout } from '../components/Callout';
import { IVOLIQ_PRICES } from '../config/competitorPrices';

function ProductShot({ src, alt, caption, priority }) {
  return (
    <figure className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 shadow-lg" data-testid="product-shot">
      <img src={src} alt={alt} loading={priority ? 'eager' : 'lazy'} decoding="async" width="1440" height="900" className="block w-full h-auto" />
      {caption && <figcaption className="px-4 py-3 text-xs text-gray-500">{caption}</figcaption>}
    </figure>
  );
}

const TOC = [
  { id: 'warum', label: 'Warum wechseln' },
  { id: 'produkt', label: 'So sieht ivoliq aus' },
  { id: 'umzug', label: 'Umzug in 5 Schritten' },
  { id: 'vergleich', label: 'Vergleich' },
  { id: 'preise', label: 'Preise' },
  { id: 'faq', label: 'FAQ' },
  { id: 'fazit', label: 'Fazit' },
];

export default function AlternativeTemplate({ cfg }) {
  const [openFaq, setOpenFaq] = useState(null);
  const { trackCTAClick, trackFAQOpen, trackPricingClick } = useTracking();
  useCalEmbed();
  useScrollTracking();

  const heroRef = useRef(null); const whyRef = useRef(null); const vergleichRef = useRef(null);
  const preiseRef = useRef(null); const faqRef = useRef(null); const finalRef = useRef(null);
  useSectionTracking('hero', heroRef); useSectionTracking('warum-alternative', whyRef);
  useSectionTracking('vergleich', vergleichRef); useSectionTracking('preise', preiseRef);
  useSectionTracking('faq', faqRef); useSectionTracking('final-cta', finalRef);

  const canonical = `https://ivoliq.io/${cfg.slug}`;
  useSEO({ title: cfg.title, description: cfg.description, canonical });
  useEffect(() => {
    const ogTags = { 'og:title': cfg.title, 'og:description': cfg.description, 'og:type': 'website', 'og:url': canonical, 'og:site_name': 'ivoliq', 'og:locale': 'de_DE', 'og:image': 'https://ivoliq.io/screens/zeiterfassung.webp', 'twitter:card': 'summary_large_image', 'twitter:title': cfg.title, 'twitter:description': cfg.description };
    Object.entries(ogTags).forEach(([prop, content]) => {
      const isOg = prop.startsWith('og:');
      let tag = document.querySelector(isOg ? `meta[property="${prop}"]` : `meta[name="${prop}"]`);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute(isOg ? 'property' : 'name', prop); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    });
  }, [cfg, canonical]);

  const toggleFaq = (i, q) => { setOpenFaq(openFaq === i ? null : i); if (openFaq !== i) trackFAQOpen(q); };
  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: cfg.faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) };
  const P = cfg.prices;

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <SoftwareApplicationSchema />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Alternativen', url: '/' }, { name: `${cfg.competitor} Alternative` }]} />
      <HowToSchema name={`Von ${cfg.migrationTool} zu ivoliq wechseln`} description={`In fünf Schritten von ${cfg.migrationTool} zu ivoliq umziehen – Kunden, Leistungen, Rechnungen und Buchungen inklusive.`} totalTime="PT30M" steps={cfg.migrationSteps.map(s => ({ name: s.title, text: s.text }))} />
      <OrganizationSchema />

      <SeoHero heroRef={heroRef} eyebrow={cfg.heroEyebrow} title={cfg.heroTitle} subtitle={cfg.heroSubtitle} bullets={cfg.heroBullets}
        actions={<>
          <Link to="/signup" onClick={() => trackCTAClick('hero', 'Kostenlos starten')} data-testid="hero-cta-register"><Button size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-8 text-base">Kostenlos starten <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          <a href="#vergleich" onClick={() => trackCTAClick('hero', 'Zum Vergleich')} data-testid="hero-cta-compare"><Button size="lg" variant="outline" className="w-full h-12 px-8 text-base">Zum Vergleich</Button></a>
        </>}
        trust={<TrustStrip />}
        mediaLabel="Echter App-Screen · Zeiterfassung"
        media={<ProductShot {...cfg.shots[0]} priority caption={null} />}
      />

      <TableOfContents items={TOC} />

      <SeoSection id="warum" variant="soft" title={cfg.whyTitle} sectionRef={whyRef} testId="why-section" width="max-w-3xl">
        <div className="space-y-5 text-base md:text-lg leading-relaxed text-gray-700">{cfg.whyParagraphs.map((p, i) => <p key={i}>{p}</p>)}</div>
      </SeoSection>

      <SeoSection id="produkt" title="So sieht ivoliq aus – echte Screens, keine Mockups" subtitle="Drei Ansichten, die den Unterschied machen: Stunden, Rechnung, Steuer-Rücklage." testId="product-section">
        <div className="grid gap-6 md:grid-cols-3">{cfg.shots.map((s, i) => <ProductShot key={i} {...s} />)}</div>
      </SeoSection>

      <MigrationSwitchSection highlightTool={cfg.migrationTool} onCtaClick={() => trackCTAClick('migration', `Jetzt von ${cfg.migrationTool} umziehen`)} />

      <SeoSection id="umzug" variant="soft" title={`Von ${cfg.migrationTool} zu ivoliq in 5 Schritten`} subtitle="Nur Schritte, die ivoliq heute wirklich kann – kein Marketing-Versprechen." testId="migration-steps-section" width="max-w-4xl">
        <ol className="space-y-4">
          {cfg.migrationSteps.map((s, i) => (
            <li key={i} className="flex gap-4 rounded-2xl bg-white p-5 ring-1 ring-gray-200" data-testid={`migration-step-${i + 1}`}>
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#062417] font-display font-bold text-white">{i + 1}</span>
              <div><h3 className="font-semibold text-gray-900">{s.title}</h3><p className="mt-1 text-sm md:text-base text-gray-600">{s.text}</p></div>
            </li>
          ))}
        </ol>
        <div className="mt-6"><Callout variant="tip" title="Belege mitnehmen">Alte Belege musst du nicht neu erfassen: Lade sie gesammelt hoch – die KI-Belegerkennung liest Betrag, Datum, USt und Lieferant automatisch aus.</Callout></div>
      </SeoSection>

      <SeoSection id="vergleich" title={`ivoliq vs. ${cfg.competitor}: Der Vergleich für Freelancer`} sectionRef={vergleichRef} testId="comparison-section">
        <ComparisonTable
          caption={`Funktionsvergleich ivoliq und ${cfg.competitorFull}`}
          columns={[{ key: 'ivoliq', label: 'ivoliq', highlight: true }, { key: 'comp', label: cfg.competitor }]}
          rows={cfg.comparisonRows}
          footnote={`Preise: Listenpreise netto, monatlich kündbar, Stand ${P.asOf}. Quelle: ${P.source}. Funktionsangaben zu ${cfg.competitorFull} nach öffentlicher Produktbeschreibung; Änderungen vorbehalten.`}
        />
      </SeoSection>

      <SeoSection id="preise" variant="soft" title={`Preise im Vergleich: ivoliq vs. ${cfg.competitor}`} subtitle={`Alle Preise netto pro Monat, Stand ${P.asOf}.`} sectionRef={preiseRef} testId="pricing-section">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-[#062417] seo-grain p-7 text-white ring-1 ring-white/10" data-testid="pricing-card-ivoliq">
            <h3 className="font-display text-2xl font-bold">ivoliq</h3>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Free</span><strong>{IVOLIQ_PRICES.free} · für immer</strong></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Basic</span><strong>9 €</strong></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Pro</span><strong>{IVOLIQ_PRICES.pro}</strong></li>
              <li className="flex justify-between text-emerald-300"><span>Pro im Jahresabo</span><strong>{IVOLIQ_PRICES.proYearly} (190 €/Jahr)</strong></li>
            </ul>
            <p className="mt-5 text-xs text-emerald-100/70">Zeiterfassung, Rechnungen, Belege und Steuer-Rücklage in jedem Plan enthalten.</p>
          </div>
          <div className="rounded-2xl bg-white p-7 ring-1 ring-gray-200" data-testid="pricing-card-competitor">
            <h3 className="font-display text-2xl font-bold text-gray-900">{cfg.competitorFull}</h3>
            <ul className="mt-5 space-y-3 text-sm text-gray-700">
              {P.plans.map(pl => <li key={pl.plan} className={`flex justify-between gap-3 border-b border-gray-100 pb-2 ${pl.plan === P.plan ? 'font-semibold text-gray-900' : ''}`}><span>{pl.plan} <span className="block text-xs font-normal text-gray-500">{pl.scope}</span></span><span className="whitespace-nowrap">{pl.price}</span></li>)}
              {P.addons?.map(a => <li key={a.name} className="flex justify-between text-gray-600"><span>+ {a.name} (Zusatzmodul)</span><span>{a.price}</span></li>)}
            </ul>
            <p className="mt-5 text-xs text-gray-500">Listenpreise netto, monatlich kündbar. Stand {P.asOf}, Quelle: <a href={P.source} rel="nofollow noopener noreferrer" target="_blank" className="underline">{P.source.replace('https://', '')}</a></p>
          </div>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <Link to="/pricing" onClick={() => trackPricingClick('preise')} data-testid="pricing-details-link"><Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">Alle ivoliq-Preise im Detail</Button></Link>
          <p className="text-sm text-gray-600">Vergleichsbasis: Buchhaltungs-Tarif „{P.plan}“ ({P.price}) vs. ivoliq Pro ({IVOLIQ_PRICES.pro}).</p>
        </div>
      </SeoSection>

      <SeoSection id="faq" title={`Häufige Fragen zur ${cfg.competitor} Alternative`} sectionRef={faqRef} testId="faq-section" width="max-w-3xl">
        <div className="space-y-3">
          {cfg.faqs.map((f, i) => (
            <div key={i} className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200" data-testid={`faq-item-${i}`}>
              <button type="button" onClick={() => toggleFaq(i, f.q)} aria-expanded={openFaq === i} className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50" data-testid={`faq-toggle-${i}`}>
                <span className="pr-4 font-semibold text-gray-900">{f.q}</span>
                <ChevronDown className={`h-5 w-5 flex-none text-gray-500 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              <div className={`px-5 pb-4 text-gray-600 leading-relaxed ${openFaq === i ? '' : 'hidden'}`}>{f.a}</div>
            </div>
          ))}
        </div>
      </SeoSection>

      <SeoSection id="fazit" variant="soft" title={`Fazit: Für wen ist ivoliq die bessere ${cfg.competitor} Alternative?`} testId="verdict-section" width="max-w-3xl">
        <p className="text-base md:text-lg leading-relaxed text-gray-700">{cfg.verdict}</p>
        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to="/zeiterfassung-freelancer" className="text-emerald-700 underline-offset-4 hover:underline">Guide: Zeiterfassung für Freelancer</Link>
          <Link to="/steuerruecklagen-freelancer" className="text-emerald-700 underline-offset-4 hover:underline">Steuerrücklagen-Rechner DE/AT/CH</Link>
          <Link to="/stundensatz-rechner" className="text-emerald-700 underline-offset-4 hover:underline">Stundensatz-Rechner</Link>
          <Link to={cfg.slug === 'lexoffice-alternative' ? '/sevdesk-alternative' : '/lexoffice-alternative'} className="text-emerald-700 underline-offset-4 hover:underline">{cfg.slug === 'lexoffice-alternative' ? 'sevDesk Alternative' : 'lexoffice Alternative'}</Link>
        </div>
      </SeoSection>

      <SeoSection variant="accent" sectionRef={finalRef} testId="final-cta-section">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Wechsel heute – Rechnung morgen aus deinen Stunden</h2>
          <p className="mt-4 text-base md:text-lg text-emerald-100/80">Kostenlos starten, 30 Tage Pro inklusive. Umzugs-Assistent für {cfg.migrationTool} ist eingebaut.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" onClick={() => trackCTAClick('final_cta', 'Jetzt kostenlos starten')} data-testid="final-cta-button"><Button size="lg" className="w-full bg-white text-[#062417] hover:bg-emerald-50 h-12 px-8 text-base font-semibold">Jetzt kostenlos starten</Button></Link>
            <Button size="lg" variant="outline" onClick={openCalModal} className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-base" data-testid="final-demo-button"><Video className="h-5 w-5 mr-2" /> Demo buchen</Button>
          </div>
          <div className="mt-8"><TrustStrip dark /></div>
        </div>
      </SeoSection>

      <Footer />
      <StickyMobileCTA onClick={() => trackCTAClick('sticky', 'Kostenlos starten')} />
    </div>
  );
}
